interface ApiResponse<T = any> {
  success: boolean
  message: string
  data?: T
  errors?: any[]
  code?: string
}

const API_ORIGIN = ((import.meta as any)?.env?.VITE_API_URL || 'https://api.revisit.pro').toString().replace(/\/$/, '')
export const API_BASE_URL = `${API_ORIGIN}/api`

console.log('VITE_API_URL from import.meta.env:', (import.meta as any).env.VITE_API_URL)
console.log('API_ORIGIN:', API_ORIGIN)
console.log('API_BASE_URL', API_BASE_URL)

// Track refresh attempts to prevent infinite loops
let refreshInProgress = false

async function attemptTokenRefresh(): Promise<boolean> {
  if (refreshInProgress) return false
  refreshInProgress = true
  
  try {
    const response = await fetch(`${API_ORIGIN}/auth/refresh`, { 
      method: 'POST', 
      credentials: 'include', 
      headers: { 'Content-Type': 'application/json' } 
    })
    return response.ok
  } catch {
    return false
  } finally {
    refreshInProgress = false
  }
}

/**
 * Raw fetch wrapper that handles headers, credentials, CSRF, and token refreshing
 * Returns the raw Response object
 */
export async function apiFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const method = (options.method || 'GET').toString().toUpperCase()
  const isUnsafe = !['GET', 'HEAD', 'OPTIONS'].includes(method)
  const isAuthEndpoint = endpoint.startsWith('/auth/')

  // Check if body is FormData - if so, don't set Content-Type (browser will set it with boundary)
  const isFormData = options.body instanceof FormData

  const defaultHeaders: Record<string, string> = {}
  if (!isFormData) {
    defaultHeaders['Content-Type'] = 'application/json'
  }

  // Ensure CSRF header and cookies for unsafe non-auth requests
  if (isUnsafe && !isAuthEndpoint) {
    try {
      let tokenCookie = ''
      try { tokenCookie = document.cookie.match(/(?:^|; )rv_csrf=([^;]+)/)?.[1] || '' } catch {}
      if (!tokenCookie && API_ORIGIN) {
        try { await attemptTokenRefresh() } catch {}
        try { tokenCookie = document.cookie.match(/(?:^|; )rv_csrf=([^;]+)/)?.[1] || '' } catch {}
      }
      if (tokenCookie) defaultHeaders['X-CSRF-Token'] = decodeURIComponent(tokenCookie)
    } catch {}
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options.headers as any),
    },
    credentials: 'include',
  }

  // Auto-stringify JSON bodies (but not FormData)
  if (config.body !== undefined && !isFormData && (config.headers as any)['Content-Type']?.includes('application/json')) {
    if (typeof config.body !== 'string') {
      try { config.body = JSON.stringify(config.body) } catch {}
    }
  }

  let response = await fetch(`${API_BASE_URL}${endpoint}`, config)

  // If request failed with 401 and it's not an auth endpoint, try to refresh token and retry
  if (response.status === 401 && !isAuthEndpoint) {
    const refreshSuccess = await attemptTokenRefresh()
    if (refreshSuccess) {
      // Update CSRF token after refresh
      try {
        const newTokenCookie = document.cookie.match(/(?:^|; )rv_csrf=([^;]+)/)?.[1] || ''
        if (newTokenCookie) {
          config.headers = {
            ...(config.headers as any),
            'X-CSRF-Token': decodeURIComponent(newTokenCookie)
          }
        }
      } catch {}
      
      // Retry the request
      response = await fetch(`${API_BASE_URL}${endpoint}`, config)
    }
  }

  return response
}

export async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const response = await apiFetch(endpoint, options)
    
    // Handle 413 Payload Too Large specifically
    if (response.status === 413) {
      return {
        success: false,
        message: 'Upload too large. Total file size must be under 20 MB. Try removing some images or reducing their size.',
        code: 'PAYLOAD_TOO_LARGE'
      }
    }
    
    const ct = response.headers.get('content-type') || ''
    const isJson = /application\/json/i.test(ct)
    const data = isJson ? await response.json() : await response.text()
    
    if (isJson) {
      return {
        success: response.ok,
        message: (data as any).message || (response.ok ? 'Success' : 'Request failed'),
        data: (data as any).data || (data as any),
        errors: (data as any).errors
      }
    }
    
    if (response.status === 401) {
      return {
        success: false,
        message: 'Session expired. Please log in again.',
        code: 'SESSION_EXPIRED'
      }
    }

    // Non-JSON (likely HTML/error). Treat as failure and include snippet for debugging
    return {
      success: false,
      message: typeof data === 'string' ? data.slice(0, 200) : 'Unexpected non-JSON response',
    }
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Network error',
    }
  }
}
